import { useState, useCallback, useEffect, useRef } from 'react';
import { useLazyQuery, DocumentNode } from '@apollo/client';
import { Dayjs } from 'dayjs';

interface ProgressiveLoadingOptions {
  dateRange: [Dayjs | null, Dayjs | null];
  query: DocumentNode;
  dataExtractor: (data: any) => any[];
  enabled?: boolean;
  chunkLimit?: number;
}

export const ProgressiveLoading = ({
  dateRange,
  query,
  dataExtractor,
  enabled = true,
  chunkLimit = 1000,
}: ProgressiveLoadingOptions) => {
  const [allData, setAllData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<any>(null);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);

  // Add a ref to track the current loading operation
  const loadingOperationRef = useRef<number>(0);

  const [executeQuery, { data: chunkData }] = useLazyQuery(query, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    onError: (err) => setError(err),
  });

  const createDateChunks = useCallback((): Array<[Dayjs, Dayjs]> => {
    if (!dateRange[0] || !dateRange[1] || !dateRange[0].isValid() || !dateRange[1].isValid()) return [];

    const chunks: Array<[Dayjs, Dayjs]> = [];
    // Consider dynamic chunking size when looking at future queries e.g appointments to avoid the
    // case when a large date range is made, and this code could potentially make 100s of requests.
    const chunkSize = 15;
    let currentStart = dateRange[0];
    const endDate = dateRange[1];

    while (currentStart.isBefore(endDate)) {
      const currentEnd = currentStart.add(chunkSize, 'day');
      const chunkEnd = currentEnd.isAfter(endDate) ? endDate : currentEnd;
      chunks.push([currentStart, chunkEnd]);
      currentStart = chunkEnd.add(1, 'day');
    }

    return chunks;
  }, [dateRange]);

  const loadChunkBatch = useCallback(
    async (
      startDate: Dayjs,
      endDate: Dayjs,
      offset: number = 0,
      operationId: number,
    ): Promise<{ data: any[]; hasMore: boolean }> => {
      // Check if this operation is still current
      if (operationId !== loadingOperationRef.current) {
        throw new Error('Operation cancelled');
      }

      try {
        const result = await executeQuery({
          variables: {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            limit: chunkLimit,
            offset: offset,
          },
        });

        // Check again after the query completes
        if (operationId !== loadingOperationRef.current) {
          throw new Error('Operation cancelled');
        }

        const extractedData = dataExtractor(result.data);
        return { data: extractedData, hasMore: extractedData.length === chunkLimit };
      } catch (err) {
        if ((err as Error).message !== 'Operation cancelled') {
          setError(err);
        }
        throw err;
      }
    },
    [executeQuery, dataExtractor, chunkLimit],
  );

  const resetState = useCallback(() => {
    setAllData([]);
    setProgress(0);
    setError(null);
    setLoading(false);
    setCurrentChunk(0);
    setTotalChunks(0);
  }, []);

  useEffect(() => {
    if (chunkData) {
      const extractedData = dataExtractor(chunkData);
      if (extractedData?.length > 0) {
        setAllData((prev) => {
          const existingIds = new Set(prev.map((item: any) => item.id || item.ida || item.uuid));
          const uniqueNewData = extractedData.filter((item: any) => {
            const itemId = item.id || item.ida || item.uuid;
            return itemId && !existingIds.has(itemId);
          });
          return uniqueNewData.length > 0 ? [...prev, ...uniqueNewData] : prev;
        });
      }
    }
  }, [chunkData, dataExtractor]);

  useEffect(() => {
    if (!enabled || !dateRange[0] || !dateRange[1] || !dateRange[0].isValid() || !dateRange[1].isValid()) {
      resetState();
      return;
    }

    const chunks = createDateChunks();
    if (chunks.length === 0) {
      resetState();
      return;
    }

    // Increment the operation ID to cancel any existing loading
    loadingOperationRef.current = (loadingOperationRef.current + 1) % Number.MAX_SAFE_INTEGER;
    const currentOperationId = loadingOperationRef.current;

    setTotalChunks(chunks.length);
    setLoading(true);
    setProgress(0);
    setAllData([]);
    setError(null);
    setCurrentChunk(0);

    const loadAllChunks = async () => {
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Check if this operation is still current
        if (currentOperationId !== loadingOperationRef.current) {
          return;
        }

        const [startDate, endDate] = chunks[chunkIndex];
        setCurrentChunk(chunkIndex + 1);

        try {
          let offset = 0;
          let hasMore = true;
          let batchCount = 0;

          while (hasMore) {
            // Check if this operation is still current
            if (currentOperationId !== loadingOperationRef.current) {
              return;
            }

            const { data, hasMore: moreData } = await loadChunkBatch(startDate, endDate, offset, currentOperationId);
            hasMore = moreData;
            offset += chunkLimit;
            batchCount++;

            if (hasMore) {
              const estimatedBatches = Math.max(batchCount + 1, Math.ceil(offset / chunkLimit) + 1);
              const subProgress = Math.min(batchCount / estimatedBatches, 0.95);
              setProgress(((chunkIndex + subProgress) / chunks.length) * 100);
            }
          }

          setProgress(((chunkIndex + 1) / chunks.length) * 100);

          if (chunkIndex < chunks.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (err) {
          if ((err as Error).message === 'Operation cancelled') {
            return;
          }
          setError(err);
          setLoading(false);
          return;
        }
      }

      // Only set loading to false if this is still the current operation
      if (currentOperationId === loadingOperationRef.current) {
        setLoading(false);
      }
    };

    loadAllChunks();

    // Cleanup function to handle component unmount or date range changes
    return () => {
      loadingOperationRef.current = (loadingOperationRef.current + 1) % Number.MAX_SAFE_INTEGER;
    };
  }, [dateRange, enabled, createDateChunks, loadChunkBatch, chunkLimit, resetState]);

  return {
    data: allData,
    loading,
    error,
    progress,
    currentChunk,
    totalChunks,
  };
};
